// Privacy Policy — kept Twilio A2P 10DLC-compliant. The SMS Communications
// section copy is required verbatim by Twilio's campaign reviewers.
// Mounted at both /privacy (legacy) and /privacy-policy (Twilio-requested).
import policyDates from '@/lib/policyDates.json';

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2
      className="text-xl sm:text-2xl font-semibold text-[#0f172a] mt-8"
      style={{ fontFamily: 'Outfit, sans-serif' }}
    >
      {title}
    </h2>
    <div className="space-y-3 text-slate-600 leading-relaxed">{children}</div>
  </section>
);

export default function PrivacyPage() {
  return (
    <div data-testid="privacy-page" className="py-16 lg:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1
          className="text-4xl sm:text-5xl font-bold tracking-tight mb-2"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Privacy Policy
        </h1>
        <p className="text-sm text-slate-500 mb-10">
          <strong>Last Updated:</strong> {policyDates.privacy}
        </p>

        <div className="prose max-w-none text-slate-600 leading-relaxed">
          <p>
            Meet For Mahjong (&ldquo;Meet For Mahjong,&rdquo; &ldquo;we,&rdquo;
            &ldquo;our,&rdquo; or &ldquo;us&rdquo;) helps American Mahjong
            players find local games, instructors, public places to play, and
            other players. This Privacy Policy explains what information we
            collect, how we use it, and the choices you have. We have written
            it in plain language so it is easy to understand.
          </p>

          <Section title="1. Information We Collect">
            <p>When you use Meet For Mahjong, we collect:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                <strong>Account information</strong> — your name or display
                name, email address, password (stored as a one-way hash), and
                phone number if you choose to provide one.
              </li>
              <li>
                <strong>Profile details</strong> — your skill level, days and
                times you&rsquo;re available, whether you have a Mahjong set,
                preferred meetup type, optional bio, and an optional profile
                photo.
              </li>
              <li>
                <strong>Location information</strong> — your ZIP code (used to
                calculate approximate distance to other players, events, and
                instructors). We do not collect precise GPS location.
              </li>
              <li>
                <strong>Event information</strong> — the events you post or
                save, including title, date, time, venue, and description.
              </li>
              <li>
                <strong>Messages and discussion board posts</strong> — the
                content of direct messages between users and posts you make on
                the Discussion Board.
              </li>
              <li>
                <strong>Payment-related information</strong> — if you purchase a
                paid membership or pay an event posting fee, our payment
                processor (currently PayPal) handles your payment details. Meet
                For Mahjong receives a confirmation of payment but does not
                store your full payment card or bank account information.
              </li>
              <li>
                <strong>Basic website usage data</strong> — sign-in times,
                pages viewed, IP address, browser type, and similar
                information used to operate and improve the site.
              </li>
            </ul>
          </Section>

          <Section title="2. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Create and manage your account.</li>
              <li>
                Help you find Mahjong events, instructors, public places to
                play, and other players near you.
              </li>
              <li>
                Send account updates such as confirmation, password resets,
                subscription renewals, and important service notices.
              </li>
              <li>
                Send event reminders and community notifications you have
                opted into.
              </li>
              <li>
                Process payments for paid memberships, event posting fees,
                or other paid features.
              </li>
              <li>Respond to support requests and questions.</li>
              <li>
                Improve the site &mdash; for example, fixing bugs, planning new
                features, and understanding which pages are most useful.
              </li>
              <li>
                Keep the community safe by reviewing reports of misconduct,
                investigating violations, and enforcing our Terms &amp;
                Conditions.
              </li>
            </ul>
          </Section>

          <Section title="3. How Your Information Is Shared">
            <p>
              Meet For Mahjong is a community site, so some information you
              choose to share is visible to other users:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                <strong>Profile information</strong> &mdash; your display name,
                profile photo, skill level, availability, general location
                (city/state and approximate distance), and bio are visible to
                other signed-in members, depending on your account settings.
              </li>
              <li>
                <strong>Events you post</strong> &mdash; event details,
                including the venue and your display name as host, are visible
                on the public events board.
              </li>
              <li>
                <strong>Discussion board posts and replies</strong> &mdash;
                posts you make on the Discussion Board are visible to signed-in
                members, and the welcome thread is visible to all members.
              </li>
              <li>
                <strong>Messages</strong> &mdash; the content of direct messages
                is only visible to you and the recipient (and to our staff if
                we need to investigate a report).
              </li>
            </ul>
            <p>
              We do not sell your personal information. We do not share your
              email address or phone number with other users. We may share
              limited information with service providers (such as our email
              delivery, SMS, and payment processors) only to the extent
              necessary to operate the service, or where required by law.
            </p>
          </Section>

          <Section title="4. Payment Information">
            <p>
              Payment processing is handled by a third-party payment provider
              (currently PayPal). When you pay for a subscription or event
              posting, you provide your payment details directly to the
              payment provider, not to Meet For Mahjong. We receive a
              confirmation of payment and a transaction reference, but we do
              not see or store your full payment card number, CVV, or bank
              account information. Please review the payment provider&rsquo;s
              own privacy policy for details on how they handle your payment
              data.
            </p>
          </Section>

          <Section title="5. SMS Communications">
            <p>
              If users opt in to receive text messages from Meet For Mahjong,
              we may use their phone number to send account updates, event
              reminders, community notifications, and other site-related
              messages. Message frequency may vary. Message and data rates may
              apply. Users can opt out at any time by replying STOP. For help,
              users can reply HELP or contact Meet For Mahjong at{' '}
              <a
                href="mailto:MeetForMahjong@gmail.com"
                className="text-[#56c6df] hover:underline"
              >
                MeetForMahjong@gmail.com
              </a>
              .
            </p>
            <p>
              Mobile opt-in data and consent are not shared with third parties
              or affiliates for marketing or promotional purposes.
            </p>
          </Section>

          <Section title="6. Cookies and Basic Analytics">
            <p>
              We use a small number of essential cookies and similar
              technologies to keep you signed in, remember your preferences,
              and operate core site features. We may also use basic analytics
              to understand which pages are popular and how to improve the
              site. We do not use third-party advertising cookies or sell your
              browsing activity.
            </p>
          </Section>

          <Section title="7. Your Choices">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                <strong>Update your account information</strong> at any time
                from your Profile page.
              </li>
              <li>
                <strong>Email preferences</strong> &mdash; transactional emails
                (such as password resets and payment confirmations) are part of
                operating your account. Other notification emails can be
                disabled from your alert preferences. Marketing-style emails
                always include an unsubscribe link.
              </li>
              <li>
                <strong>SMS opt-out</strong> &mdash; reply STOP to any text
                message from Meet For Mahjong to stop receiving SMS, or remove
                your phone number / disable SMS alerts in your account
                settings.
              </li>
              <li>
                <strong>Account deletion</strong> &mdash; you may request
                deletion of your account by contacting us at the address
                below. We will remove or anonymize your personal information,
                subject to limited retention for fraud prevention, dispute
                resolution, and legal obligations.
              </li>
            </ul>
          </Section>

          <Section title="8. Children's Privacy">
            <p>
              Meet For Mahjong is intended for adults. We do not knowingly
              collect personal information from children under 13. If you
              believe a child has provided us information, please contact us
              and we will delete it.
            </p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. We will
              update the &ldquo;Last Updated&rdquo; date at the top of this
              page and, for material changes, will give notice through the
              site or by email.
            </p>
          </Section>

          <Section title="10. Contact Us">
            <p>
              If you have any questions about this Privacy Policy or how your
              information is handled, please contact us at{' '}
              <a
                href="mailto:MeetForMahjong@gmail.com"
                className="text-[#56c6df] hover:underline"
              >
                MeetForMahjong@gmail.com
              </a>
              .
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
